import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PackageStatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { PackageOpen, MapPin, Phone, User } from "lucide-react";

export default function ForPickup() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dispatcher/packages/for-pickup")
      .then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="for-pickup-page" className="space-y-4">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ For Pick-Up</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Packages For Pick-Up</h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm">{loading ? "Loading…" : `${rows.length} package${rows.length === 1 ? "" : "s"} ready`}</p>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-14 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <PackageOpen className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-base text-slate-700 dark:text-slate-300 mb-1">Nothing waiting yet</div>
          <div className="text-xs text-slate-500">Scan QR codes to mark packages for pickup.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(p => (
            <div
              key={p.id}
              data-testid={`for-pickup-${p.package_id}`}
              className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-mono-tech text-xs text-cyan-300 truncate">{p.qr_code_id}</div>
                  <div className="font-mono-tech text-[11px] text-slate-500 truncate">{p.package_id}</div>
                </div>
                <PackageStatusBadge status={p.status} />
              </div>
              <div className="font-display font-semibold text-slate-900 dark:text-white">{p.package_name}</div>
              <div className="mt-2 space-y-0.5 text-sm">
                <div className="text-slate-200 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-slate-500" /> {p.receiver_name}
                </div>
                <div className="text-slate-600 dark:text-slate-400 flex items-center gap-1.5 text-xs">
                  <Phone className="w-3 h-3 text-slate-500" /> {p.receiver_phone}
                </div>
                <div className="text-slate-600 dark:text-slate-400 flex items-start gap-1.5 text-xs">
                  <MapPin className="w-3 h-3 mt-0.5 text-slate-500 shrink-0" /> <span>{p.receiver_address}</span>
                </div>
              </div>
              <div className="mt-3 pt-2 border-t border-slate-200/60 dark:border-slate-800/60 text-[10px] text-slate-500 font-mono-tech flex justify-between">
                <span>{(p.items || []).length} items</span>
                <span>{new Date(p.updated_at).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
